import { Currency } from "@redotech/money/currencies";
import { useConfirmationSummary } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-confirmation-summary";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ReturnItemParams } from "@redotech/redo-customer-portal-shared/util";
import { UpsellBillingTrigger } from "@redotech/redo-model/return";
import { ReturnTotals } from "@redotech/redo-model/return-totals-calculator";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { capitalizeWords } from "@redotech/string/util";
import { SummaryCard } from "../../submission/components/summary-card";

export function SummaryCardConfirmation({
  totals,
  currency,
  feesHeader,
  feesOrderHeader,
  products,
  newItems,
  labelDeducted,
  settings,
  upsellBillingTrigger,
}: {
  feesHeader: string;
  feesOrderHeader: string;
  totals: ReturnTotals;
  currency: Currency;
  products: ReturnItemParams[] | undefined;
  newItems: ReturnItemParams[] | undefined;
  labelDeducted: boolean;
  settings: CustomerPortalAppSettings | undefined;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
}) {
  const { isLoading, productSubtotals } = useConfirmationSummary({
    totals,
    products,
  });

  const {
    isLoading: isLoadingNewItems,
    productSubtotals: newOrderItemsSubtotals,
  } = useConfirmationSummary({ totals, products: newItems });

  if (isLoading || isLoadingNewItems) {
    return <LoadingRedoAnimation />;
  }
  return (
    <SummaryCard
      adjustmentValue={totals.totalAdjustmentValue}
      adjustmentValueString={capitalizeWords(
        settings?.returnAdjustment[
          totals.totalAdjustmentValue > 0 ? "positiveName" : "negativeName"
        ] ?? "",
      )}
      currency={currency}
      exchangeShippingFee={totals.exchangeShippingFee}
      exchangeShippingName={totals.exchangeShippingName}
      feesHeader={feesHeader}
      feesOrderHeader={feesOrderHeader}
      labelDeducted={labelDeducted}
      newOrderItemsSubtotals={newOrderItemsSubtotals}
      newOrderTaxes={[
        { title: "Taxes", total: totals.advancedExchangeItemsTaxInNewOrder },
      ]}
      newOrderValue={totals.newOrderValue}
      newVariantExchangeItemSubtotals={[]}
      pickupFee={undefined}
      pointsDeductedFromRefundSubtotal={
        totals.totalReturnValueDeductedDueToLoyaltyPoints
          ? {
              title: "Loyalty Points Deducted",
              total: -totals.totalReturnValueDeductedDueToLoyaltyPoints,
            }
          : undefined
      }
      productSubtotals={productSubtotals}
      returnValue={totals.totalReturnValue - totals.totalAdjustmentValue}
      shippingFee={totals.shippingFee}
      shippingFeeDeducted={totals.shippingFeeDeducted}
      submitted
      taxSubtotals={[{ title: "Taxes", total: totals.totalTaxes }]}
      totalLoyaltyPointsDeducted={totals.totalLoyaltyPointsDeducted}
      totalLoyaltyPointsRefunded={totals.totalLoyaltyPointsRefunded}
      totalPaymentOwed={totals.totalPaymentOwed}
      totalRefund={totals.totalRefundWithoutShipping}
      totalShippingFee={totals.shippingFee + totals.exchangeShippingFee}
      totalStoreCredit={totals.totalStoreCredit}
      upsellBillingTrigger={upsellBillingTrigger}
    />
  );
}
